import React, { useEffect, useState } from 'react';
import axios from 'axios';
import useSeo from '@/hooks/useSeo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public tutorials page — shows every published tutorial in a 3-column grid
 * (single column on mobile). Each card is a YouTube embed + title + short
 * description. Hidden from nav when there are zero tutorials (the nav
 * component checks `/api/tutorials` count on mount).
 */
export default function Tutorials() {
  const [tutorials, setTutorials] = useState([]);
  const [loading, setLoading] = useState(true);
  // Header copy is admin-editable. Hardcode the same defaults the backend uses
  // so the page never flashes empty.
  const [header, setHeader] = useState({
    title: 'Tutorials',
    subtitle: 'Quick walkthroughs, tips and ideas to get the most out of Visual Atmos — from your first seamless loop to advanced ambient renders.',
  });

  useSeo({
    title: 'Tutorials — Learn Visual Atmos | Cozy Loop & AI Video Tips',
    description: 'Step-by-step video tutorials for Visual Atmos. Learn how to make seamless cozy loops, generate AI videos with stable camera, and get the most out of every credit.',
    canonical: 'https://visualatmos.com/tutorials',
  });

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      axios.get(`${API}/tutorials`),
      axios.get(`${API}/tutorials-header`),
    ])
      .then(([listRes, headerRes]) => {
        if (cancelled) return;
        setTutorials(listRes.data?.tutorials || []);
        if (headerRes.data?.title) setHeader({
          title: headerRes.data.title,
          subtitle: headerRes.data.subtitle || '',
        });
      })
      .catch(() => {
        if (!cancelled) setTutorials([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-[#0F1419] text-white px-4 sm:px-6 py-10" data-testid="tutorials-page">
      <div className="max-w-6xl mx-auto">
        <header className="mb-10">
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight" data-testid="tutorials-page-title">{header.title}</h1>
          {header.subtitle && (
            <p className="mt-3 text-gray-400 max-w-2xl text-base sm:text-lg whitespace-pre-line" data-testid="tutorials-page-subtitle">
              {header.subtitle}
            </p>
          )}
        </header>

        {loading ? (
          <p className="text-gray-500 text-sm">Loading…</p>
        ) : tutorials.length === 0 ? (
          <p className="text-gray-500 text-sm">No tutorials yet — check back soon.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="tutorials-grid">
            {tutorials.map((t, i) => (
              <article
                key={`${t.embed_url}-${i}`}
                className="rounded-2xl overflow-hidden bg-[#1A2035] border border-[#2A3550] hover:border-[#FFB238]/60 transition-colors"
                data-testid="tutorial-card"
              >
                <div className="relative aspect-video bg-black">
                  <iframe
                    src={t.embed_url}
                    title={t.title}
                    className="absolute inset-0 w-full h-full"
                    loading="lazy"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  />
                </div>
                <div className="p-5">
                  <h2 className="text-lg font-bold text-white leading-snug" data-testid="tutorial-title">{t.title}</h2>
                  {t.description && (
                    <p className="mt-2 text-sm text-gray-400 leading-relaxed">{t.description}</p>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}

        {!loading && tutorials.length > 0 && (
          <div className="mt-12 flex justify-center" data-testid="tutorials-youtube-follow">
            <a
              href="https://www.youtube.com/@visual-atmos"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-[#FF0000] hover:bg-[#E60000] text-white font-bold text-sm sm:text-base shadow-lg shadow-[#FF0000]/20 transition-colors"
              data-testid="tutorials-youtube-follow-link"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.546 15.568V8.432L15.818 12l-6.272 3.568z" />
              </svg>
              Follow Us on YouTube
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
